import { useEffect, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { XMLParser } from "fast-xml-parser";
import { postSoapRequest } from "../../services/TypeAnnonce_service";
import type { TabStypeItem } from "../../models/typeAnnonce";

const customArrivalAnnonce: TabStypeItem = {
  nType: 2,
  nSType: 22,
  sSType: "Transfert siège hors département (Annonce d'arrivée)",
};

const normalizeSlug = (value: string) =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const normalizeMode = (value: string | undefined) => {
  const mode = (value || "").toLowerCase();
  if (mode === "libre" || mode === "l") return "libre";
  if (mode === "assiste" || mode === "assite" || mode === "a") return "assiste";
  return "assiste";
};

const normalizeText = (s?: string) => (s || "").replace(/\u00A0/g, " ").trim().toLowerCase();
const stripAccents = (s: string) => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
const clearExternalAnnonceRedirect = () => {
  sessionStorage.removeItem("postLoginRedirect");
  sessionStorage.removeItem("externalAnnonceParams");
};

const applyTypeAnnonceRules = (rawItems: TabStypeItem[], checkedIndex: number): TabStypeItem[] => {
  const groups: TabStypeItem[][] = [[], [], [], []];

  rawItems.forEach((item) => {
    const nType = Number(item?.nType);
    if (nType >= 1 && nType <= 4) groups[nType - 1].push(item);
  });

  groups[1] = groups[1].map((item) => {
    if (item.sSType === "Transfert de siège hors département") {
      return {
        ...item,
        sSType: "Transfert siège hors département (Annonce de départ)",
      };
    }
    return item;
  });

  const departIndex = groups[1].findIndex(
    (item) => item.sSType === "Transfert siège hors département (Annonce de départ)"
  );

  if (departIndex !== -1) {
    const arrivalExists = groups[1].some((item) => item.sSType === customArrivalAnnonce.sSType);
    if (!arrivalExists) {
      groups[1].splice(departIndex + 1, 0, customArrivalAnnonce);
    }
  } else if (!groups[1].some((item) => item.sSType === customArrivalAnnonce.sSType)) {
    groups[1].push(customArrivalAnnonce);
  }

  const excludedNamesNoAccent = new Set(["marches publics", "adjudications"]);

  const constitution = groups[0].filter((item) => item.sSType !== "Création autre hors forfait");
  const modification = groups[1].filter((item) => {
    if (item.sSType === "Modification Autre") return false;
    if (checkedIndex === 1 && item.sSType === "Fusion") return false;
    if (checkedIndex === 1 && item.sSType === "Transformation Autre") return false;
    return true;
  });
  const cessation = groups[2];
  const divers = groups[3].filter((item) => {
    const sNorm = normalizeText(item.sSType);
    const sNoAccent = stripAccents(sNorm);

    if (excludedNamesNoAccent.has(sNoAccent)) return false;
    if (sNorm === "annonce additive" || sNorm === "annonce rectificative") return false;
    if (checkedIndex === 1 && (sNorm === "avis administratifs" || sNorm === "autre (rédaction libre)")) {
      return false;
    }
    return true;
  });

  return [...constitution, ...modification, ...cessation, ...divers];
};

const ExternalAnnonceSlugEntry = () => {
  const navigate = useNavigate();
  const location = useLocation(); // ✅ AJOUT
  const params = useParams<{ mode: string; typeAnnonce: string }>();
  const hasRun = useRef(false);

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;

    // ✅ AJOUT — Détection brouillon depuis utilisateur connecté
    const isAuthenticated =
      !!localStorage.getItem("token") && !!localStorage.getItem("userId");
    const state = location.state as { fromDraft?: boolean; draftId?: string } | null;

    if (isAuthenticated && state?.fromDraft) {
      // Utilisateur connecté qui modifie un brouillon
      // → rester dans le Layout, préserver la session et le menu
      navigate("/home/nouvelle-annonce", {
        state: { fromDraft: true, draftId: state.draftId },
        replace: true,
      });
      return; // ← stopper ici, ne pas exécuter le flux anonyme
    }
    // ✅ FIN AJOUT

    const init = async () => {
      try {
        const mode = normalizeMode(params.mode);
        const requestedTypeSlug = normalizeSlug(params.typeAnnonce || "");
        const checkedIndexValue = mode === "libre" ? 0 : 1;

        const responseXml = await postSoapRequest();
        const parser = new XMLParser();
        const jsonResponse = parser.parse(responseXml.data);
        const rawTabStype =
          jsonResponse?.["SOAP-ENV:Envelope"]?.["SOAP-ENV:Body"]?.["Get_nSTypeResult"]?.tabStype;

        const tabStypeArray: TabStypeItem[] = Array.isArray(rawTabStype)
          ? rawTabStype
          : rawTabStype
            ? [rawTabStype]
            : [];

        const allowedItems = applyTypeAnnonceRules(tabStypeArray, checkedIndexValue);
        const matchedType = allowedItems.find(
          (item) => normalizeSlug(item.sSType || "") === requestedTypeSlug
        );

        if (!matchedType) {
          clearExternalAnnonceRedirect();
          navigate("/anonymous-login", { replace: true });
          return;
        }

        const activeIndexTarget = 1;

        sessionStorage.setItem(
          "postLoginRedirect",
          JSON.stringify({
            path: "/home/nouvelle-annonce",
            state: {
              fromExternal: true,
              externalStep: activeIndexTarget,
              externalMode: checkedIndexValue,
              externalType: {
                nType: Number(matchedType.nType),
                nSType: Number(matchedType.nSType),
                sSType: matchedType.sSType,
              },
            },
          })
        );

        sessionStorage.setItem(
          "externalAnnonceParams",
          JSON.stringify({
            externalStep: activeIndexTarget,
            externalMode: checkedIndexValue,
            externalType: {
              nType: Number(matchedType.nType),
              nSType: Number(matchedType.nSType),
              sSType: matchedType.sSType,
            },
          })
        );

        navigate("/anonymous-login", { replace: true });
      } catch (error) {
        console.error("Erreur traitement URL type annonce:", error);
        clearExternalAnnonceRedirect();
        navigate("/anonymous-login", { replace: true });
      }
    };

    init();
  }, [navigate, location.state, params.mode, params.typeAnnonce]);

  return (
    <div className="div-spinner">
      <div className="spinner-square">
        <div className="square-1 square"></div>
        <div className="square-2 square"></div>
        <div className="square-3 square"></div>
      </div>
      <p style={{ marginTop: 16, color: "#555" }}>Redirection en cours...</p>
    </div>
  );
};

export default ExternalAnnonceSlugEntry;